import { useState, useEffect } from "react";
import axios from "axios";
import {
  TrendingUp,
  TrendingDown,
  PieChart as PieChartIcon,
  Globe,
  DollarSign,
  Shield,
  ChevronRight,
  Sparkles,
  Info,
  Award,
  AlertTriangle,
  Loader2,
  Building2,
  Target,
  RefreshCw,
  Activity,
  BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Grade color mapping
const GRADE_COLORS = {
  A: "bg-green-500",
  B: "bg-lime-500",
  C: "bg-amber-500",
  D: "bg-orange-500",
  F: "bg-red-500",
};

// Chart line colors
const LINE_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4"];

export const FundDeepDive = ({ fundName, isOpen, onClose }) => {
  const [fundData, setFundData] = useState(null);
  const [livePrices, setLivePrices] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chartPeriod, setChartPeriod] = useState("1y");

  useEffect(() => {
    if (isOpen && fundName) {
      fetchFundData();
    }
  }, [isOpen, fundName]);

  useEffect(() => {
    if (fundData && isOpen) {
      fetchLivePrices();
      fetchChartData();
    }
  }, [fundData, chartPeriod]);

  const fetchFundData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/fund-intelligence/${encodeURIComponent(fundName)}`);
      setFundData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch fund data");
    } finally {
      setLoading(false);
    }
  };

  const fetchLivePrices = async () => {
    setPricesLoading(true);
    try {
      const response = await axios.get(`${API}/fund-intelligence/${encodeURIComponent(fundName)}/live-prices`);
      setLivePrices(response.data);
    } catch (err) {
      console.error("Failed to fetch live prices:", err);
    } finally {
      setPricesLoading(false);
    }
  };

  const fetchChartData = async () => {
    setChartLoading(true);
    try {
      const response = await axios.get(`${API}/fund-intelligence/${encodeURIComponent(fundName)}/performance-chart?period=${chartPeriod}`);
      setChartData(response.data);
    } catch (err) {
      console.error("Failed to fetch chart data:", err);
    } finally {
      setChartLoading(false);
    }
  };

  // Transform chart data for Recharts
  const getChartLines = () => {
    if (!chartData?.data) return { data: [], tickers: [] };
    
    const dateMap = {};
    chartData.data.forEach(d => {
      if (!dateMap[d.date]) dateMap[d.date] = { date: d.date };
      dateMap[d.date][d.ticker] = d.normalized;
    });
    
    return {
      data: Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)),
      tickers: chartData.tickers || [],
    };
  };

  if (!isOpen) return null;

  const chartLines = getChartLines();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="fund-deep-dive-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            {fundName}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="ml-2">Analyzing fund...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-500">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            {error}
          </div>
        )}

        {fundData && (
          <div className="space-y-6">
            {/* Health Score Header */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Health Score Card */}
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-muted-foreground">Health Score</span>
                    <Badge className={`${GRADE_COLORS[fundData.health_score?.grade] || "bg-gray-500"} text-white text-lg px-3`}>
                      {fundData.health_score?.grade || "N/A"}
                    </Badge>
                  </div>
                  <div className="text-4xl font-bold">{fundData.health_score?.overall_score || 0}/100</div>
                  <Progress 
                    value={fundData.health_score?.overall_score || 0} 
                    className="mt-3 h-2"
                  />
                </CardContent>
              </Card>

              {/* Fund Type Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Fund Type</span>
                  </div>
                  <div className="text-2xl font-semibold">{fundData.fund_type}</div>
                  <div className="text-sm text-muted-foreground mt-1">
                    OCF: {fundData.ocf}%
                  </div>
                  {fundData.active_premium && (
                    <div className="text-xs text-amber-500 mt-1">
                      +{fundData.active_premium}% vs passive alternative
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Concentration Card */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <PieChartIcon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Top 10 Concentration</span>
                  </div>
                  <div className="text-2xl font-semibold">{fundData.top_holding_concentration}%</div>
                  <Progress 
                    value={fundData.top_holding_concentration} 
                    className="mt-3 h-2"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Fund Performance Summary (from live prices) */}
            {livePrices?.fund_performance && (
              <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border-blue-500/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-blue-500">Live Performance (Weighted)</span>
                    </div>
                    {pricesLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                  <div className="grid grid-cols-5 gap-4">
                    {[
                      { label: "1D", value: livePrices.fund_performance.weighted_return_1d },
                      { label: "1W", value: livePrices.fund_performance.weighted_return_1w },
                      { label: "1M", value: livePrices.fund_performance.weighted_return_1m },
                      { label: "3M", value: livePrices.fund_performance.weighted_return_3m },
                      { label: "1Y", value: livePrices.fund_performance.weighted_return_1y },
                    ].map((item) => (
                      <div key={item.label} className="text-center">
                        <div className="text-xs text-muted-foreground">{item.label}</div>
                        <div className={`text-lg font-bold ${item.value >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {item.value >= 0 ? '+' : ''}{item.value?.toFixed(1)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* AI Summary */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                  <div>
                    <span className="text-sm font-medium text-primary">AI Assessment</span>
                    <p className="text-sm mt-1">{fundData.health_score?.ai_summary}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs for detailed views */}
            <Tabs defaultValue="performance" className="w-full">
              <TabsList className="grid grid-cols-5 w-full">
                <TabsTrigger value="performance">Performance</TabsTrigger>
                <TabsTrigger value="holdings">Holdings</TabsTrigger>
                <TabsTrigger value="sectors">Sectors</TabsTrigger>
                <TabsTrigger value="geography">Geography</TabsTrigger>
                <TabsTrigger value="scores">Scores</TabsTrigger>
              </TabsList>

              <TabsContent value="performance" className="mt-4 space-y-4">
                {/* Performance Chart */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" /> Holdings Performance
                      </CardTitle>
                      <div className="flex gap-1">
                        {["1m", "3m", "6m", "1y"].map((p) => (
                          <Button
                            key={p}
                            variant={chartPeriod === p ? "default" : "ghost"}
                            size="sm"
                            className="text-xs px-2 h-7"
                            onClick={() => setChartPeriod(p)}
                          >
                            {p.toUpperCase()}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <CardDescription>Top 5 holdings normalized to 100 at period start</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {chartLoading ? (
                      <div className="h-64 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin" />
                      </div>
                    ) : chartLines.data.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartLines.data}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 10 }}
                              tickFormatter={(d) => d.slice(5)}
                            />
                            <YAxis 
                              tick={{ fontSize: 10 }}
                              domain={['auto', 'auto']}
                              tickFormatter={(v) => `${v}`}
                            />
                            <Tooltip 
                              formatter={(value, name) => [`${value?.toFixed(1)}`, name]}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            {chartLines.tickers.map((ticker, idx) => (
                              <Line
                                key={ticker}
                                type="monotone"
                                dataKey={ticker}
                                stroke={ticker === "FUND" ? "#fff" : LINE_COLORS[idx % LINE_COLORS.length]}
                                strokeWidth={ticker === "FUND" ? 3 : 1.5}
                                dot={false}
                                name={ticker}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-muted-foreground">
                        No chart data available
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {chartLines.tickers.map((ticker, idx) => (
                        <Badge 
                          key={ticker} 
                          variant="outline"
                          style={{ 
                            borderColor: ticker === "FUND" ? "#fff" : LINE_COLORS[idx % LINE_COLORS.length],
                            color: ticker === "FUND" ? "#fff" : LINE_COLORS[idx % LINE_COLORS.length]
                          }}
                        >
                          {ticker}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top/Worst Performers */}
                {livePrices && (
                  <div className="grid grid-cols-2 gap-4">
                    <Card className="border-green-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-green-500 flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" /> Top Performers (1D)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {livePrices.top_performers_1d?.map((h, idx) => (
                          <div key={idx} className="flex justify-between py-1 text-sm">
                            <span>{h.name}</span>
                            <span className="text-green-500 font-medium">+{h.change?.toFixed(1)}%</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                    <Card className="border-red-500/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-red-500 flex items-center gap-2">
                          <TrendingDown className="w-4 h-4" /> Laggards (1D)
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {livePrices.worst_performers_1d?.map((h, idx) => (
                          <div key={idx} className="flex justify-between py-1 text-sm">
                            <span>{h.name}</span>
                            <span className="text-red-500 font-medium">{h.change?.toFixed(1)}%</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="holdings" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Holdings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(livePrices?.holdings || fundData.holdings)?.slice(0, 10).map((holding, idx) => (
                        <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                          <div className="flex items-center gap-3">
                            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                              {idx + 1}
                            </span>
                            <div>
                              <div className="font-medium text-sm">{holding.name}</div>
                              <div className="text-xs text-muted-foreground">{holding.ticker}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-medium">{holding.weight}%</div>
                            {holding.price && (
                              <div className="text-xs text-muted-foreground">${holding.price}</div>
                            )}
                            {holding.change_1d_pct !== undefined && holding.change_1d_pct !== null && (
                              <div className={`text-xs ${holding.change_1d_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                {holding.change_1d_pct >= 0 ? '+' : ''}{holding.change_1d_pct?.toFixed(1)}%
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="sectors" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Sector Exposure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={fundData.sector_exposure}
                            dataKey="weight"
                            nameKey="sector"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ sector, weight }) => `${sector}: ${weight}%`}
                            labelLine={false}
                          >
                            {fundData.sector_exposure?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value}%`} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {fundData.sector_exposure?.map((sector, idx) => (
                        <Badge key={idx} variant="outline" style={{ borderColor: sector.color }}>
                          <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: sector.color }} />
                          {sector.sector}: {sector.weight}%
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="geography" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Geographic Exposure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPie>
                          <Pie
                            data={fundData.geography_exposure}
                            dataKey="weight"
                            nameKey="region"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ region, weight }) => `${region}: ${weight}%`}
                            labelLine={false}
                          >
                            {fundData.geography_exposure?.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => `${value}%`} />
                        </RechartsPie>
                      </ResponsiveContainer>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {fundData.geography_exposure?.map((geo, idx) => (
                        <Badge key={idx} variant="outline" style={{ borderColor: geo.color }}>
                          <span className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: geo.color }} />
                          {geo.region}: {geo.weight}%
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="scores" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Health Score Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { label: "Momentum", value: fundData.health_score?.momentum_score },
                      { label: "Diversification", value: fundData.health_score?.diversification_score },
                      { label: "Cost Efficiency", value: fundData.health_score?.cost_efficiency_score },
                      { label: "Macro Alignment", value: fundData.health_score?.macro_alignment_score },
                      { label: "Risk-Adjusted", value: fundData.health_score?.risk_adjusted_score },
                    ].map((score, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between text-sm mb-1">
                          <span>{score.label}</span>
                          <span className="font-medium">{score.value}/100</span>
                        </div>
                        <Progress value={score.value} className="h-2" />
                      </div>
                    ))}

                    <div className="pt-4 border-t">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-green-500 mb-2 flex items-center gap-1">
                            <Award className="w-4 h-4" /> Strengths
                          </h4>
                          <ul className="text-sm space-y-1">
                            {fundData.health_score?.strengths?.map((s, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-green-500">•</span>
                                {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-amber-500 mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" /> Concerns
                          </h4>
                          <ul className="text-sm space-y-1">
                            {fundData.health_score?.concerns?.map((c, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-amber-500">•</span>
                                {c}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Passive Alternative */}
            {fundData.passive_alternative && (
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm text-amber-500 font-medium">Passive Alternative</span>
                      <p className="text-sm">{fundData.passive_alternative}</p>
                      <p className="text-xs text-muted-foreground">OCF: {fundData.passive_ocf}% (saves {fundData.active_premium}% annually)</p>
                    </div>
                    <Info className="w-5 h-5 text-amber-500" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FundDeepDive;
